'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Course } from '@eliadmin/core/src/api/courses/coursers-api';
type CourseWithMetadata = Course & {
  metadata?: {
    agencyName?: string;
    courseLocation?: string;
  };
};
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { Star } from 'lucide-react';
import { useState } from 'react';
import { CategoryBadge } from '../jobportal/card/category-badge';
import { AddToFavourite } from '../jobportal/card/add-to-favorites';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';

export type CourseCardProps = {
  course: CourseWithMetadata;
  className?: {
    main?: string;
  };
  isBought?: boolean;
  priority?: boolean;
  headingLevel?: 'h2' | 'h4';
};

function CourseCardInternal({ course, priority = false, headingLevel: Heading = 'h4' }: CourseCardProps) {
  const { t } = useTranslations();
  const me = useCustomerStore(s => s.me);
  const [imageError, setImageError] = useState(false);

  const showFavoriteButton = me ? true : false;

  const isTopped = course?.topped_up_to && new Date(course.topped_up_to).getTime() > Date.now();
  const productPrice = course.product ? getProductPriceDisplay(course.product) : null;

  return (
    <div
      className={cn(
        'relative col-span-1 w-full overflow-hidden rounded-[24px]',
        'curriculum-vitae-card__shadow',
        isTopped && 'border-[3px] border-primary hover:border-primary',
      )}
    >
      {showFavoriteButton && (
        <AddToFavourite
          course={course as any}
          type="course"
          className={{
            container: cn('absolute right-6 top-6 z-[10] h-9 w-9', isTopped && 'top-10'),
          }}
        />
      )}
      <Link
        prefetch={false}
        href={course.slug}
        className={cn(
          'curriculum-vitae-card__shadow col-span-1 w-full overflow-hidden rounded-[24px]',
        )}
      >
        <div
          className={cn(
            'relative h-[200px] w-full sm:h-[230px]',
            isTopped && 'border-b-[3px] border-primary',
          )}
        >
          <Image
            src={
              imageError
                ? '/images/placeholder.png'
                : (course?.image?.url ?? '/images/placeholder.png')
            }
            alt={resolveImageAlt(
              imageError ? undefined : course?.image,
              course?.name ?? 'User name',
            )}
            fill
            style={{ objectFit: 'cover', objectPosition: 'center' }}
            sizes="(min-width: 1536px) 380px, (min-width: 1280px) 25vw, (min-width: 768px) 30vw, (min-width: 640px) 50vw, 100vw"
            priority={priority}
            fetchPriority={priority ? 'high' : undefined}
            onError={() => setImageError(true)}
            onLoad={() => setImageError(false)}
          />
          {!!isTopped && (
            <div className="absolute right-0 top-0 z-[10] flex h-[30px] w-auto items-center justify-center rounded-bl-lg bg-primary px-6 text-sm font-bold text-white">
              TOP
            </div>
          )}
        </div>
        <div className="flex h-[calc(100%-200px)] min-h-[200px] w-full flex-col items-start justify-between gap-2 bg-white p-6 sm:h-[calc(100%-230px)]">
          <div className="flex w-full flex-col gap-2">
            <div className="flex w-full flex-wrap items-start justify-start gap-2">
              {course?.categories?.map(category => (
                <CategoryBadge
                  key={category?.name}
                  item={{
                    name: category?.name,
                    slug: category?.name?.toLowerCase().replace(/\s+/g, '-') || '',
                  }}
                  asLink={false}
                />
              ))}
            </div>
            {/* <h4 className="text-base font-medium tracking-widest text-muted-foreground">
            TATRA BANKA a.s.
          </h4> */}
            <Heading
              data-ea-visual-tag={Heading === 'h2' ? 'h4' : undefined}
              className="line-clamp-2 h-[54px] text-lg font-bold text-foreground"
            >
              {course.name}
            </Heading>

            {/* Agency Name and Location from Metadata */}
            <div className="flex min-h-[44px] flex-col gap-2 text-sm text-muted-foreground">
              {course?.metadata?.agencyName && course.metadata.agencyName.trim() && (
                <div className="flex flex-col gap-1">
                  <span className="font-medium">Agentúra:</span>
                  <div className="grid grid-cols-1 gap-1">
                    {course.metadata.agencyName
                      .split(',')
                      .slice(0, 2)
                      .filter(agency => agency.trim())
                      .map((agency: string, index: number) => (
                        <span key={index} className="truncate">
                          {agency.trim()}
                        </span>
                      ))}
                  </div>
                </div>
              )}
              {course?.metadata?.courseLocation && course.metadata.courseLocation.trim() && (
                <div className="flex flex-col gap-1">
                  <span className="font-medium">Miesto:</span>
                  <div className="grid grid-cols-1 gap-1">
                    {course.metadata.courseLocation
                      .split(',')
                      .slice(0, 2)
                      .filter(location => location.trim())
                      .map((location: string, index: number) => (
                        <span key={index} className="truncate">
                          {location.trim()}
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Price - only show if not 0 */}
          {productPrice?.priceOnRequest && (
            <div className="text-lg font-semibold text-primary">{t('text2278')}</div>
          )}
          {!productPrice?.priceOnRequest && (productPrice?.current ?? 0) > 0 && (
            <div className="text-lg font-semibold text-primary">
              {formatPrice(productPrice?.current, '€')}
            </div>
          )}
          {isTopped && (
            <Star className="absolute bottom-[-50px] right-[-40px] h-40 w-40 text-yellow-400 opacity-20" />
          )}
          {/* <LocationBadge
          countries={[
            {
              name: course.country.name,
              id: course.country.id,
              is_default: course.country.is_default,
            },
          ]}
        /> */}
        </div>
      </Link>
    </div>
  );
}

export function CourseCard(props: CourseCardProps) {
  const providerKey = props.course.product?.id
    ? `course-product-${props.course.product.id}`
    : `course-${props.course.slug}`;

  // This card only displays data and a favorite action; none of its descendants
  // consumes variant/dialog context. Keep the reset key without two stores/card.
  return <CourseCardInternal key={providerKey} {...props} />;
}
